// LLM generated - Model Claude 4 Sonnet
import { useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { NotificationType } from '../notifications.types';
import type { Notification } from '../notifications.types';

export interface NotificationAuthor {
  handle: string;
  avatarUrl?: string;
  displayName?: string;
  role?: 'commissioner' | 'user' | 'system';
}

export interface NotificationPayload {
  header: string;
  message: string;
  contestName?: string;
  contestId?: string;
}

export interface NotificationTimestamp {
  formatted: string;
  raw: Date | null;
  isValid: boolean;
}

export interface NotificationData {
  author: NotificationAuthor;
  payload: NotificationPayload;
  timestamp: NotificationTimestamp;
}

/**
 * Consolidated hook that extracts all notification data in a single memoized operation
 *
 * Extracts author information, payload content, and formatted timestamps with consistent
 * fallback logic across all notification types.
 *
 * @param notification - The notification to extract data from
 * @returns NotificationData object with author, payload, and timestamp information
 */
export const useNotificationData = (notification: Notification): NotificationData => {
  const { t } = useTranslation('notifications');

  return useMemo(() => {
    // Author extraction logic
    const author: NotificationAuthor = (() => {
      // Commissioner post messages have special author handling
      if (notification.type === NotificationType.CONTEST_POST_MESSAGE_PUBLISHED) {
        const postAuthor =
          notification.payload && 'post' in notification.payload
            ? notification.payload.post?.author
            : null;

        const handle = postAuthor?.handle || notification.senderChannel?.handle || 'commissioner';
        const avatarUrl = postAuthor?.avatarUrl || notification.senderChannel?.avatarUrl;

        return {
          handle,
          avatarUrl,
          displayName: postAuthor?.handle || 'Commissioner',
          role: 'commissioner',
        };
      }

      // Standard notification author resolution
      const handle = notification.senderChannel?.handle;
      const avatarUrl = notification.senderChannel?.avatarUrl;

      return {
        handle: handle || 'system',
        avatarUrl,
        displayName: handle || 'System',
        role: handle ? 'user' : 'system',
      };
    })();

    // Payload extraction logic
    const payload: NotificationPayload = (() => {
      // Commissioner post messages have specific payload structure
      if (notification.type === NotificationType.CONTEST_POST_MESSAGE_PUBLISHED) {
        const notificationPayload =
          notification.payload && 'post' in notification.payload ? notification.payload : null;

        return {
          header:
            notificationPayload?.post?.header || t('notifications.fallbacks.commissionerMessage'),
          message:
            notificationPayload?.post?.message ||
            notification.fallbackMessage ||
            t('notifications.modals.commissionerPost.content.defaultMessage'),
          contestName: notificationPayload?.contest?.name,
          contestId: notificationPayload?.contest?.id,
        };
      }

      // Default fallback for other notification types
      return {
        header: t('notifications.fallbacks.notificationTitle'),
        message: notification.fallbackMessage || t('notifications.fallbacks.contentNotAvailable'),
      };
    })();

    // Timestamp formatting logic
    const timestamp: NotificationTimestamp = (() => {
      if (!notification.createdAt) {
        return {
          formatted: '',
          raw: null,
          isValid: false,
        };
      }

      const date = new Date(notification.createdAt);

      // Check if date is valid
      if (Number.isNaN(date.getTime())) {
        return {
          formatted: '',
          raw: null,
          isValid: false,
        };
      }

      // Format with consistent pattern across notifications
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });

      return {
        formatted,
        raw: date,
        isValid: true,
      };
    })();

    return {
      author,
      payload,
      timestamp,
    };
  }, [notification, t]);
};
